/* eslint-disable spire/export-styles */
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeMenuStyles } from "@insite/content-library/Components/NavigationModeMenu";
import { Hidden, Link, Typography } from "@insite/mobius/index";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import useEmblaCarousel from "embla-carousel-react";
import React, { FC, useCallback } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    enableWarehousePickup: getSettingsCollection(state).accountSettings.enableWarehousePickup,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const Container = styled.div`
    overflow: hidden;
    background-color: ${getColor("primary")};
    .embla__container {
        display: flex;
        height: 28px;
    }
    .embla__slide {
        flex: 0 0 100%;
        min-width: 0;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .flexslider-slides-text {
        color: #ffffff;
        font-size: 13px;
        &.top-contact {
            margin-left: 4px;
        }
        strong {
            color: #ffffff;
        }
    }
`;

export interface SecondaryNavigationStyles {
    wrapper?: InjectableCss;
    navigationModeMenu?: NavigationModeMenuStyles;
    languageLink?: InjectableCss;
    warehousePickupLink?: InjectableCss;
    signInLink?: InjectableCss;
}

export const secondaryNavigationStyles: SecondaryNavigationStyles = {
    languageLink: {
        css: css`
            flex: 0 0 auto;
            margin-left: 30px;
        `,
    },
    warehousePickupLink: {
        css: css`
            flex: 0 1 auto;
            margin-left: 30px;
            min-width: 0;
        `,
    },
    signInLink: {
        css: css`
            flex: 0 0 auto;
            margin-left: 30px;
        `,
    },
};

const defaultCustomCss = `.wrapper {
}

.navigation-mode-menu {
}

.currency-menu {
}

.language-menu {
}

.ship-to-address-menu {
}

.sign-in-link {
}`;

const SecondaryNavigation: FC<Props> = ({ id, enableWarehousePickup, fields }) => {
    const styles = useMergeStyles("secondaryNavigation", secondaryNavigationStyles);

    const [emblaRef, embla] = useEmblaCarousel({ loop: true });

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }
        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, 8000);

    return (
        <Hidden below="lg">
            <Container className="embla" ref={emblaRef}>
                <StyledWrapper className="embla__container">
                    <StyledWrapper className="embla__slide">
                        <StyledWrapper>
                            <Typography className="flexslider-slides-text">
                                <strong>{siteMessage("Get SAME-DAY SHIPPING on In Stock orders placed by 2pm local time")} </strong>
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                    
                    <StyledWrapper className="embla__slide">
                        <StyledWrapper>
                            <Typography className="flexslider-slides-text">
                                <strong>24/7 Technical Support Available </strong>
                            </Typography>
                            <Link className="top-contact flexslider-slides-text " href="tel:1-800-318-4618">
                                <strong>(1-800-318-4618)</strong>
                            </Link>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper className="embla__slide">
                        <Typography className="flexslider-slides-text">
                            {translate(
                                "Due to supply constraints some inventory may be reserved for critical needs with contract customers. Please contact our chat team for assistance",
                            )}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            </Container>
        </Hidden>
    );
};

const secondaryNavigation: WidgetModule = {
    component: connect(mapStateToProps)(SecondaryNavigation),
    definition: {
        group: "Common",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default secondaryNavigation;